import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  CreditCard, 
  Calendar, 
  DollarSign,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Settings,
  X
} from 'lucide-react';

const SubscriptionManagement = ({ memberEmail, onClose }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (memberEmail) {
      fetchSubscriptionStatus();
    }
  }, [memberEmail]);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/subscriptions/status/${memberEmail}`);
      const data = await response.json();

      if (response.ok && data.success) {
        setSubscription(data.subscription);
      } else {
        throw new Error(data.detail || 'Failed to fetch subscription status');
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const response = await fetch(`${backendUrl}/api/subscriptions/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          member_email: memberEmail,
          reason: cancelReason || 'User requested cancellation'
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Show success message and refresh subscription status
        alert('✅ Subscription cancelled successfully! Your account has been downgraded to Basic membership.');
        
        // Update localStorage to reflect Basic membership
        const basicMember = JSON.parse(localStorage.getItem('basicMemberApplication') || '{}');
        basicMember.membershipLevel = 'Basic';
        localStorage.setItem('basicMemberApplication', JSON.stringify(basicMember));
        
        // Refresh subscription status
        await fetchSubscriptionStatus();
        setShowCancelConfirm(false);
        
        // Optionally close the modal after a short delay
        setTimeout(() => {
          if (onClose) onClose();
          // Reload the page to reflect changes
          window.location.reload();
        }, 2000);
      } else {
        throw new Error(data.detail || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setError(error.message);
    } finally {
      setCancelling(false);
    }
  };

  const getBillingPortal = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/subscriptions/billing-portal/${memberEmail}`);
      const data = await response.json();

      if (response.ok && data.success) {
        // Open billing portal in new window
        window.open(data.portal_url, '_blank');
      } else {
        alert('Unable to access billing portal at this time. Please contact support.');
      }
    } catch (error) {
      console.error('Error accessing billing portal:', error);
      alert('Unable to access billing portal at this time. Please contact support.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'cancelled':
        return 'red';
      case 'free':
        return 'gray';
      default:
        return 'yellow';
    }
  };

  const getMembershipColor = (level) => {
    switch (level) {
      case 'Standard':
        return 'amber';
      case 'Premium':
        return 'blue';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Loading subscription details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Error Loading Subscription</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Settings className="w-6 h-6" />
              <span>Subscription Management</span>
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Subscription Overview */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Current Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Membership Level:</span>
                    <Badge className={`bg-${getMembershipColor(subscription?.membership_level)}-100 text-${getMembershipColor(subscription?.membership_level)}-800`}>
                      {subscription?.membership_level}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Status:</span>
                    <Badge className={`bg-${getStatusColor(subscription?.status)}-100 text-${getStatusColor(subscription?.status)}-800`}>
                      {subscription?.status}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Billing:</span>
                    <span className="text-sm font-medium">
                      {subscription?.status === 'free' 
                        ? 'Free' 
                        : `$${subscription?.amount}/${subscription?.billing_frequency}`
                      }
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Billing Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {subscription?.status === 'free' ? (
                    <div className="text-center py-4">
                      <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">
                        Basic membership is completely free!
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <div>
                          <p className="text-sm text-gray-600">Next Billing Date:</p>
                          <p className="text-sm font-medium">
                            {subscription?.next_billing_date 
                              ? new Date(subscription.next_billing_date).toLocaleDateString()
                              : 'N/A'
                            }
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <DollarSign className="w-4 h-4 text-gray-500" />
                        <div>
                          <p className="text-sm text-gray-600">Amount:</p>
                          <p className="text-sm font-medium">
                            ${subscription?.amount} {subscription?.currency}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <CreditCard className="w-4 h-4 text-gray-500" />
                        <div>
                          <p className="text-sm text-gray-600">Subscription ID:</p>
                          <p className="text-xs font-mono text-gray-500">
                            {subscription?.subscription_id?.slice(0, 20)}...
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Action Buttons */}
          {subscription?.status === 'active' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Subscription Actions</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <Button 
                  onClick={getBillingPortal}
                  variant="outline" 
                  className="w-full"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Manage Billing Details
                </Button>
                
                <Button 
                  onClick={() => setShowCancelConfirm(true)}
                  variant="destructive" 
                  className="w-full"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Cancel Subscription
                </Button>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Changes to your billing details should be made through the 
                  billing portal. If you cancel your subscription, your account will be downgraded 
                  to Basic membership at the end of your current billing period.
                </p>
              </div>
            </div>
          )}

          {/* Cancel Confirmation Dialog */}
          {showCancelConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60">
              <Card className="w-full max-w-md mx-4">
                <CardHeader>
                  <CardTitle className="text-red-600">Cancel Subscription</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Are you sure you want to cancel your {subscription?.membership_level} membership? 
                    Your account will be immediately downgraded to Basic membership.
                  </p>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Reason for cancellation (optional):
                    </label>
                    <textarea
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      rows="3"
                      placeholder="Help us improve by telling us why you're cancelling..."
                    />
                  </div>
                  
                  <div className="flex space-x-3">
                    <Button 
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                      variant="destructive" 
                      className="flex-1"
                    >
                      {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                    </Button>
                    <Button 
                      onClick={() => setShowCancelConfirm(false)}
                      variant="outline" 
                      className="flex-1"
                    >
                      Keep Subscription
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Basic Membership Benefits */}
          {subscription?.status === 'free' && (
            <Card className="bg-green-50 border-green-200">
              <CardHeader>
                <CardTitle className="text-green-800">Your Basic Benefits</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-2">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">Legal documents & forms</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">Educational resources</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">FREE GC & Contractor sign-up</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">Member community access</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionManagement;